import React, { useState } from 'react';
import { useDesignStore } from '../../stores/designStore';
import { useTemplateStore } from '../../stores/templateStore';
import { TemplateSelector } from '../template';
import { PublishModal, PagesManager } from '../publish';
import type { DeviceType, ThemeConfig } from '../../types';

interface ToolbarButtonProps {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ active, onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    className={`
      p-2 rounded-lg transition-all duration-200
      ${active 
        ? 'bg-purple-100 text-purple-600' 
        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
      }
    `}
  >
    {children}
  </button>
);

export const DesignerToolbar: React.FC = () => {
  const { 
    currentDesign, 
    currentDevice, 
    themeConfig,
    setCurrentDevice, 
    updateThemeConfig,
    refreshPreview,
    previewHtml,
    isGenerating,
  } = useDesignStore();
  
  const { getCurrentTemplate, publishedPages } = useTemplateStore();
  const currentTemplate = getCurrentTemplate();

  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPagesManager, setShowPagesManager] = useState(false);

  // 设备切换
  const handleDeviceChange = (device: DeviceType) => {
    setCurrentDevice(device);
  };

  // 刷新预览
  const handleRefresh = () => {
    refreshPreview();
  };

  // 主题色选择
  const handleColorChange = (colorKey: keyof ThemeConfig, color: string) => {
    updateThemeConfig({ [colorKey]: color });
  };

  // 预设主题色
  const presetColors = [
    '#667eea', '#764ba2', '#f093fb', '#f5576c',
    '#4facfe', '#00f2fe', '#43e97b', '#38f9d7',
    '#fa709a', '#fee140', '#30cfd0', '#330867',
    '#a8edea', '#fed6e3', '#d299c2', '#fef9d7',
  ];

  const devices: { type: DeviceType; icon: React.ReactNode; label: string }[] = [
    {
      type: 'desktop',
      label: '桌面端',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      type: 'tablet',
      label: '平板',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      type: 'mobile',
      label: '手机',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <div className="h-14 bg-white border-b flex items-center justify-between px-4">
        {/* 左侧：设计名称 + 模板信息 */}
        <div className="flex items-center gap-3">
          <h2 className="font-medium text-gray-900 truncate max-w-[200px]">
            {currentDesign?.name || '未命名设计'}
          </h2>
          
          {/* 当前模板标签 */}
          <button
            onClick={() => setShowTemplateSelector(true)}
            className="flex items-center gap-1.5 text-xs px-2 py-1 bg-purple-50 text-purple-600 rounded-full hover:bg-purple-100 transition-colors"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
            </svg>
            {currentTemplate.name}
          </button>
          
          {isGenerating && (
            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded-full flex items-center gap-1">
              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              生成中...
            </span>
          )}
        </div>

        {/* 中间：设备切换 + 刷新 */}
        <div className="flex items-center gap-2">
          {/* 设备切换 */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            {devices.map((device) => (
              <ToolbarButton
                key={device.type}
                active={currentDevice === device.type}
                onClick={() => handleDeviceChange(device.type)}
                title={device.label}
              >
                {device.icon}
              </ToolbarButton>
            ))}
          </div>

          <div className="w-px h-6 bg-gray-200 mx-2" />

          {/* 刷新按钮 */}
          <ToolbarButton onClick={handleRefresh} title="刷新预览">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </ToolbarButton>

          {/* 主题色选择器 */}
          <div className="relative">
            <ToolbarButton onClick={() => setShowThemePicker(!showThemePicker)} title="主题设置">
              <div 
                className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: themeConfig.primaryColor }}
              />
            </ToolbarButton>

            {/* 主题色下拉面板 */}
            {showThemePicker && (
              <>
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setShowThemePicker(false)}
                />
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border p-4 z-50">
                  <div className="text-sm font-medium text-gray-700 mb-3">主题色</div>
                  <div className="grid grid-cols-4 gap-2">
                    {presetColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleColorChange('primaryColor', color)}
                        className={`
                          w-10 h-10 rounded-lg transition-all
                          ${themeConfig.primaryColor === color ? 'ring-2 ring-offset-2 ring-purple-500' : 'hover:scale-110'}
                        `}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t">
                    <label className="text-xs text-gray-500 mb-2 block">自定义颜色</label>
                    <input
                      type="color"
                      value={themeConfig.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      className="w-full h-8 rounded cursor-pointer"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 右侧：页面管理 + 发布按钮 */}
        <div className="flex items-center gap-2">
          {/* 页面管理按钮 */}
          {publishedPages.length > 0 && (
            <button
              onClick={() => setShowPagesManager(true)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              页面管理
              <span className="bg-purple-100 text-purple-600 text-xs px-1.5 py-0.5 rounded-full">
                {publishedPages.length}
              </span>
            </button>
          )}

          <div className="w-px h-6 bg-gray-200" />

          {/* 发布按钮 */}
          <button
            onClick={() => setShowPublishModal(true)}
            disabled={!previewHtml}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all
              ${!previewHtml
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg hover:scale-105'
              }
            `}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            发布
          </button>
        </div>
      </div>

      {/* 模板选择器弹窗 */}
      <TemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        onSelect={(templateId) => {
          console.log('Selected template:', templateId);
        }}
      />

      {/* 发布弹窗 */}
      <PublishModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onPublish={(result) => {
          if (result.success) {
            console.log('Published:', result.url);
          } else {
            console.error('Publish failed:', result.error);
          }
        }}
      />

      {/* 页面管理弹窗 */}
      <PagesManager
        isOpen={showPagesManager}
        onClose={() => setShowPagesManager(false)}
      />
    </>
  );
};
